import { isArray, map } from 'underscore';
import { useState, useCallback, useEffect, useRef, useContext } from 'react';
import {
  InstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';

import { LocationService } from './location-service';
import { DEFAULT_BING_MAP_API_KEY, DEFAULT_GEO_LOCATION } from './utils';

export const useLocationsFromBingMapsApi = (apiKey = DEFAULT_BING_MAP_API_KEY) => {
  const isInit = useRef(true);
  const [service, setService] = useState(new LocationService({ apiKey }));
  const logger = useContext(InstrumentationServiceContext);

  const getLocations = useCallback((query) => {
    logger.logTrace('BingMapLocation');

    // Bing map API has limit on query length,
    // so only use part of location to get the geoLocation info
    const trimmedQuery = String(query).trim().slice(0, 120);

    return service.query(trimmedQuery).then((res) => {
      logger.logTrace('BingMapGetLocation-Succeed');
      // Always make sure has a result
      const locationRes = {
        isFallbackLoc: true, // indicate if the location is default
        geoLocation: DEFAULT_GEO_LOCATION,
      };

      if (res && isArray(res.resourceSets)) {
        const firstResourceSet = res.resourceSets[0];
        if (firstResourceSet && isArray(firstResourceSet.resources)) {
          for (let index = 0; index < firstResourceSet.resources.length; index += 1) {
            const location = firstResourceSet.resources[index];

            if (location && location.point
              && isArray(location.point.coordinates)
              && location.confidence !== 'Low'
            ) {
              locationRes.geoLocation = {
                latitude: location.point.coordinates[0],
                longitude: location.point.coordinates[1],
              };
              locationRes.isFallbackLoc = false;

              break;
            }
          }
        }
      }

      return locationRes;
    }).catch(() => {
      logger.logTrace(`BingMapGetLocation-Fail-${query}`);

      return {
        isFallbackLoc: true,
        geoLocation: DEFAULT_GEO_LOCATION,
      };
    });
  }, [logger, service]);

  const getAutoSuggestedLocations = useCallback(
    (query, max) => {
      logger.logTrace('BingMapAutoSuggestion');

      const trimmedQuery = String(query).trim();

      return service.autoSuggest(trimmedQuery, max).then((res) => {
        logger.logTrace('BingMapAutoSuggestion-Succeed');

        let suggestedLocations = [];

        if (res && isArray(res.resourceSets)) {
          const firstResourceSet = res.resourceSets[0];

          if (firstResourceSet && isArray(firstResourceSet.resources)) {
            const suggestedResults = firstResourceSet.resources[0];

            if (suggestedResults && isArray(suggestedResults.value)) {
              suggestedLocations = map(
                suggestedResults.value,
                item => ({
                  // eslint-disable-next-line no-underscore-dangle
                  key: `${item.name}-${item.__type}-${item.address.formattedAddress}`,
                  name: item.name || '',
                  formattedAddress: item.address.formattedAddress,
                  // formattedAddress could be same, so append "name" to differentiate them
                  completeAddress: `${item.name ? `${item.name}, ` : ''}${item.address.formattedAddress}`,
                  rawAddress: item.address,
                })
              );
            }
          }
        }

        return suggestedLocations;
      }).catch(() => {
        logger.logTrace(`BingMapAutoSuggestion-Fail-${query}`);

        return [];
      });
    },
    [service, logger]
  );

  useEffect(() => {
    if (isInit.current) {
      isInit.current = false;
    } else {
      setService(new LocationService({ apiKey }));
    }
  }, [apiKey]);

  return {
    getAutoSuggestedLocations,
    getLocations,
  };
};
